import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import LoginPage from "@/pages/LoginPage";
import InboxPage from "@/pages/InboxPage";
import AdminPage from "@/pages/AdminPage";
import { ensureServiceWorker, installNotificationNavHandler } from "@/lib/pushClient";
import { useIsMobile } from "@/hooks/useIsMobile";

const ProtectedRoute = ({ children, allowedRoles }) => {
    const { user, loading } = useAuth();
    if (loading) {
        return (
            <div className="h-full flex items-center justify-center bg-slate-50" data-testid="auth-loading">
                <div className="text-slate-500 font-display">Cargando…</div>
            </div>
        );
    }
    if (!user) return <Navigate to="/login" replace />;
    if (allowedRoles && !allowedRoles.includes(user.role)) {
        return <Navigate to="/inbox" replace />;
    }
    return children;
};

/**
 * Register the ViajaHub service worker on first load AND wire the
 * notification-click bridge so tapping an OS push jumps the user to the
 * right conversation without a full reload. Both are idempotent.
 */
function ServiceWorkerBootstrap() {
    const navigate = useNavigate();
    useEffect(() => {
        ensureServiceWorker();
        installNotificationNavHandler((url) => {
            // Convert absolute "/inbox?conversation_id=xyz" → react-router nav.
            try {
                const u = new URL(url, window.location.origin);
                navigate(u.pathname + u.search, { replace: false });
            } catch (_e) {
                navigate("/inbox");
            }
        });
    }, [navigate]);
    return null;
}

function AppRoutes() {
    return (
        <>
            <ServiceWorkerBootstrap />
            <Routes>
                <Route path="/login" element={<LoginPage />} />
                <Route path="/inbox" element={<ProtectedRoute><InboxPage /></ProtectedRoute>} />
                <Route path="/admin/*" element={
                    <ProtectedRoute allowedRoles={["admin"]}><AdminPage /></ProtectedRoute>
                } />
                <Route path="*" element={<Navigate to="/inbox" replace />} />
            </Routes>
        </>
    );
}

function App() {
    useEffect(() => { document.title = "ViajaHub"; }, []);
    // Position + offset must be viewport-aware — the desktop 3-column layout
    // wants toasts in the top-right corner (out of the reading flow), but on
    // mobile that same position was landing under the iOS Dynamic Island /
    // notch AND under the mobile TopBar (Level 1 hamburger row), so the
    // action button ("Abrir") ended up in dead pixels. Fix (Jul-20-2026):
    //   - Mobile (<768 px): `top-center` + offset ≈ safe-area + 88 px so the
    //     toast lands squarely below the TopBar with a comfortable margin,
    //     centered so it's reachable with either thumb.
    //   - Desktop (≥768 px): unchanged — `top-right` + 20 px offset.
    // The toast content sizing is applied via `toastOptions.classNames.toast`
    // — max-width 100vw-32px on mobile so both edges have breathing room,
    // and min-h[44px] on the action button + close button per Apple HIG.
    const isMobile = useIsMobile();
    return (
        <div className="App h-full">
            <BrowserRouter>
                <AuthProvider>
                    <AppRoutes />
                    <Toaster
                        richColors
                        position={isMobile ? "top-center" : "top-right"}
                        offset={
                            isMobile
                                ? "calc(env(safe-area-inset-top, 0px) + 88px)"
                                : 20
                        }
                        expand
                        duration={4000}
                        toastOptions={{
                            classNames: {
                                toast: isMobile
                                    ? "!max-w-[calc(100vw-32px)] !w-[calc(100vw-32px)]"
                                    : "",
                                // 44×44 tap targets per Apple HIG. Sonner's
                                // defaults are 24-28 px which iOS thumb users
                                // can barely hit. `!important` overrides
                                // sonner's own class ordering.
                                actionButton: "!min-h-[44px] !px-4 !text-sm",
                                cancelButton: "!min-h-[44px] !px-4 !text-sm",
                                closeButton: "!w-11 !h-11",
                            },
                        }}
                    />
                </AuthProvider>
            </BrowserRouter>
        </div>
    );
}

export default App;
